import "bootstrap/dist/css/bootstrap.min.css";
import { createApp } from "vue";
import App from "./App.vue";
import router, { reloadRoutes, isReloading } from "./router";  // 修改1: 导入 isReloading
import { createStore } from "vuex";
import VueZoomer from 'vue-zoomer';
import i18n from './locales/index';
import ElementPlus from 'element-plus';
import 'element-plus/dist/index.css';
import SplitCarousel from "vue-split-carousel";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import VueToast from 'vue-toast-notification';
import { modules, dataState } from "./store/storeModules.js";

import PriceFormatter from "./plugins/PriceFormatter.js";
import DateFormatter from "./plugins/DateFormatter.js";
import FileSource from "./plugins/FileSource.js";
import Slugerize from "./plugins/Slugerize.js";

import moment from 'moment';
import "./icons/iconsSolid.js";
import "./icons/iconsRegular.js";
import "./icons/iconsBrands.js";

import 'vue-toast-notification/dist/theme-sugar.css';
import "./assets/css/components.css";

import { platform } from '@/platform'

const app = createApp(App);

const store = new createStore({
  modules,
  strict: process.env.NODE_ENV !== "production",
  plugins: [dataState]
});

// 初始化全局标志
window.__IS_NAVIGATING_AFTER_RELOAD = false;

// ============ 核心：安全的路由重载函数 ============
let isReloadingRoutes = false;
const safeReloadRoutes = async (userRole = 'guest') => {
  //  检查两个标志
  if (isReloadingRoutes || isReloading) {  // 同时检查两个标志
    console.log('⏳ Route reload already in progress, skipping...');
    return false;
  }

  isReloadingRoutes = true;
  try {
    console.log('🔄 Reloading routes for role:', userRole);
    const result = reloadRoutes(userRole);

    // 等待路由更新完成
    await new Promise(resolve => setTimeout(resolve, 200));

    // 验证路由加载成功
    const routes = router.getRoutes();
    console.log('✅ Routes reloaded, total routes:', routes.length);

    isReloadingRoutes = false;
    return result;

  } catch (error) {
    console.error('❌ Error reloading routes:', error);
    isReloadingRoutes = false;
    return false;
  }
};

// ============ 应用启动函数 ============
async function startApplication() {
  try {
    console.log('🚀 Starting application...');
    if (platform.isWebView) {
      try {
        const urlParams = new URLSearchParams(window.location.search)
        const token = urlParams.get('token')

        if (token) {
          await store.dispatch('user/loginByUrlToken', token)
        } else {
          await store.dispatch('user/clearUserAndDegrade')
        }
      } catch (error) {
        console.error('❌ 自动登录失败:', error)
      }
    } else {
      console.log('🌐 在 H5 环境中，跳过 URL token 登录')
    }

    const currentRole = store.getters['user/userRole'] || 'guest';
    console.log('🔄 Initializing routes for role:', currentRole);
    await safeReloadRoutes(currentRole);

    try {
      store.dispatch('fetchContactInfo')
    } catch (err) {
      console.log(err, 115)
    }

    app.use(VueZoomer)
      .use(router)
      .use(store)
      .use(i18n)
      .use(PriceFormatter)
      .use(FileSource)
      .use(VueToast)
      .use(Slugerize)
      .use(DateFormatter)
      .use(SplitCarousel)
      .use(ElementPlus)
      .component("font-awesome-icon", FontAwesomeIcon);

    app.config.globalProperties.$moment = moment;
    app.mount("#app");
    console.log('✅ Application mounted');

  } catch (error) {
    console.error('❌ Failed to start application:', error);
    app.mount("#app");
  }
}

// ============ 监听用户状态变化 ============
store.subscribe((mutation, state) => {
  console.log('🔄 Store mutation:', mutation.type);

  if (mutation.type.startsWith('user/')) {
    const userUpdateMutations = [
      'user/SET_AUTH',
      'user/SET_USER',
      'user/CLEAR_USER'
    ];

    if (userUpdateMutations.includes(mutation.type)) {
      const userAuth = state.user?.auth || {};
      const userRole = userAuth.role || 'guest';

      console.log('👤 User updated, new role:', userRole);

      setTimeout(async () => {
        await safeReloadRoutes(userRole);
      }, 50);
    }
  }
});

// ============ 修改4: 路由守卫 ============
router.beforeEach(async (to, from, next) => {
  const t = i18n.global.t;
  window.scrollTo(0, 0);

  if (store && store.commit) {
    store.commit("app/forceCloseModal");
  }

  const isAuthenticated = store.getters['user/isAuthenticated'];
  const userRole = store.getters['user/userRole'] || 'guest';

  console.log('🔍 Route guard check:', {
    to: to.path,
    isAuthenticated: isAuthenticated,
    userRole: userRole,
    requiresAuth: to.meta?.requiresAuth || false,
    isNavigatingAfterReload: window.__IS_NAVIGATING_AFTER_RELOAD  // 添加日志
  });

  // 修改5: 关键修复 - 如果是重载后的导航，直接放行
  if (window.__IS_NAVIGATING_AFTER_RELOAD) {
    console.log('🔄 This is navigation after reload, allowing:', to.path);
    // 注意：不要重置标志，让 router.replace 的 finally 中重置
    next();
    return;
  }

  // 特殊处理：如果已经在登录页且已登录，直接重定向
  if (to.path === '/login' && isAuthenticated) {
    console.log('🔄 Already logged in, redirecting from login page');
    const redirectParam = to.query.redirect;
    if (redirectParam) {
      next(redirectParam);
    } else if (userRole === 'admin') {
      next('/dashboard');
    } else {
      next('/');
    }
    return;
  }

  // 检查路由是否存在
  const routeExists = router.hasRoute(to.name) ||
    router.getRoutes().some(r => r.path === to.path);

  if (!routeExists) {
    console.log('🔄 Route not found:', to.path);

    // 如果是已登录用户，尝试重新加载路由
    if (isAuthenticated) {
      console.log('🔄 User is authenticated, reloading routes...');
      await safeReloadRoutes(userRole);

      await new Promise(resolve => setTimeout(resolve, 150));
      const newRouteExists = router.hasRoute(to.name) ||
        router.getRoutes().some(r => r.path === to.path);

      if (newRouteExists) {
        console.log('✅ Route restored, proceeding...');
        next();
        return;
      }
    }

    console.log('❌ Route still not found, redirecting to 404');
    next('/404');
    return;
  }

  // 认证检查
  if (to.meta?.requiresAuth && !isAuthenticated) {
    console.log('🔐 Authentication required, redirecting to login');
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    });
    return;
  }

  // 权限检查
  if (to.meta?.roles && userRole) {
    const hasPermission = to.meta.roles.includes(userRole);
    if (!hasPermission) {
      console.log('🚫 Insufficient permissions');
      next({ name: 'Home' });
      return;
    }
  }

  console.log('✅ Access granted');
  next();
});

// ============ 页面刷新处理 ============
window.addEventListener('beforeunload', () => {
  const currentPath = window.location.pathname;
  const isAuthenticated = store.getters['user/isAuthenticated'];

  // 获取当前路由的 meta 信息
  const currentRoute = router.currentRoute.value;
  const noRestore = currentRoute.meta?.noRestore === true;  // ✅ 检查标记

  // 只保存普通页面（没有 noRestore 标记的页面）
  if (isAuthenticated && currentPath !== '/' && !noRestore) {
    console.log('💾 Saving page state:', currentPath);
    sessionStorage.setItem('last_authenticated_path', currentPath);
    sessionStorage.setItem('last_path_save_time', Date.now().toString());
  } else {
    // 如果是特殊页面，清除之前保存的状态
    sessionStorage.removeItem('last_authenticated_path');
    sessionStorage.removeItem('last_path_save_time');
  }
});

// 页面加载后恢复状态
window.addEventListener('load', () => {
  setTimeout(() => {
    const savedPath = sessionStorage.getItem('last_authenticated_path');
    const saveTime = sessionStorage.getItem('last_path_save_time');
    const isAuthenticated = store.getters['user/isAuthenticated'];
    const currentPath = window.location.pathname;

    // 获取当前路由的 meta 信息
    const currentRoute = router.currentRoute.value;
    const currentNoRestore = currentRoute.meta?.noRestore === true;

    if (savedPath && saveTime && isAuthenticated && !currentNoRestore) {
      const timeDiff = Date.now() - parseInt(saveTime);

      if (timeDiff < 5000) {
        console.log('🔄 Restoring previous page:', savedPath);
        sessionStorage.removeItem('last_authenticated_path');
        sessionStorage.removeItem('last_path_save_time');

        if (window.location.pathname !== savedPath) {
          // 检查要恢复的页面是否也允许恢复
          const savedRoute = router.getRoutes().find(r => r.path === savedPath);
          const savedNoRestore = savedRoute?.meta?.noRestore === true;

          if (!savedNoRestore) {
            router.replace(savedPath);
          }
        }
      }
    }
  }, 300);
});

startApplication();

export default app;